import { formatTempoLabel } from "../bpm";
import type { CoachConfig } from "../engine/coach-params";
import type { HandSplitOptions } from "../engine/hand-split";
import { type MidiPort, shortMidiName } from "../midi/input";
import type { CatalogEntry } from "../songs/catalog";
import type { DisplayOptions, UIMode } from "../store/prefs";
import type { ScoreRecord } from "../store/scores";
import type { HandFilter } from "../types";
import { buildCalibrationControls } from "./calibration-controls";
import { buildChainDots, type ChainProgress } from "./chain-dots";
import { buildCoachNumFields } from "./coach-fields";
import { buildCredits } from "./credits";
import { checkbox, el, field, fieldWith, group, rowBetween, selectOf } from "./dom";
import { buildLoopControls } from "./loop-controls";
import { buildMetronomeControls } from "./metronome-controls";
import { buildKeyHints } from "./piano-legend";
import { buildRecordControl } from "./record-control";
import { renderResults } from "./results-view";
import { renderCatalogOptions, renderDeviceOptions, repaintCatalogCache } from "./select-options";
import { buildStaffControls } from "./staff-controls";

// UI / transport. Plain typed DOM — no framework.
// The top bar is status-only (song title, play state, tempo readout, metronome,
// loop, MIDI); transport runs from the keyboard live-strip, with the mouse controls
// (play/pause, tempo) in the drawer. A hamburger flush-right opens the drawer.

interface ControlHandlers {
  onCatalogPick(id: string): void;
  /** The ▶ Next button: load the suggested id, or shortcut an active countdown. */
  onPlayNext(id: string): void;
  onFile(file: File): void;
  onPlayPause(): void;
  onRestart(): void;
  onTempo(multiplier: number): void;
  onLoopSet(which: "a" | "b"): void;
  onLoopClear(): void;
  onTranspose(semitones: number): void;
  onMetronome(on: boolean): void;
  onMetronomeVolume(volume: number): void;
  onCountIn(beats: number): void;
  /** Sound the unplayed hand through MIDI-out while practising (F1). */
  onAccompaniment(on: boolean): void;
  onPlayForMe(): void;
  /** Toggle performance recording (→ MIDI export). */
  onToggleRecord(): void;
  onCoachConfig(patch: Partial<CoachConfig>): void;
  onMode(mode: UIMode): void;
  onHand(hand: HandFilter): void;
  onHandOverride(opts: HandSplitOptions): void;
  onDevice(id: string): void;
  onWhiteWidth(px: number): void;
  onOffset(px: number): void;
  onDisplay(display: DisplayOptions): void;
  onPianoLiveKeys(on: boolean): void;
  onClearResults(): void;
  /** Open the discovery map; show the path to the current song (standalone path view). */
  onOpenMap(): void;
  onShowPath(): void;
}

interface ControlsApi {
  setCatalog(entries: CatalogEntry[]): void;
  /** Repaint catalog option labels with cached/downloadable state. */
  setCached(ids: Set<string>): void;
  /** Show the curriculum "play next" hint that loads `id` on click (null hides it). */
  setPlayNext(text: string | null, id?: string): void;
  setDevices(ports: MidiPort[], selectedId: string | null): void;
  setPlaying(playing: boolean): void;
  setStatus(text: string): void;
  /** Show the connected MIDI device (or null = none) in the transport. */
  setMidi(deviceName: string | null): void;
  setNowPlaying(attribution: string): void;
  /** The current song's title, shown in the slim top bar (the picker moved into the drawer). */
  setCurrentSong(title: string): void;
  /** Select the picker dropdown to `id` (a catalog id; "" / non-catalog clears it). */
  setSelectedSong(id: string): void;
  setCalibration(whiteWidthPx: number, offsetPx: number): void;
  setMode(mode: UIMode): void;
  /** Show the effective play mode (Wait / Play / Coach / Demo) in the top bar. */
  setModeLabel(label: string): void;
  /** Show the current song's per-chain mastery dots (empty ⇒ hidden, e.g. manual modes). */
  setChainDots(chains: ChainProgress[]): void;
  setTempo(multiplier: number): void;
  /** Publish the loaded song's base tempo (BPM) for the display-only readout. */
  setSongBpm(bpm: number | null): void;
  setLoop(start: number | null, end: number | null): void;
  setTranspose(semitones: number): void;
  setMetronome(on: boolean): void;
  setMetronomeVolume(volume: number): void;
  setCountIn(beats: number): void;
  setAccompaniment(on: boolean): void;
  /** Reflect recording state on the Record button (`enabled` = a MIDI device is present). */
  setRecording(recording: boolean, enabled: boolean): void;
  setCoachConfig(cfg: CoachConfig): void;
  setHand(hand: HandFilter): void;
  setHandOverride(opts: HandSplitOptions): void;
  setDisplay(display: DisplayOptions): void;
  setPianoLiveKeys(on: boolean): void;
  /** The drawer element, so the command layer can open it and walk its cells. */
  readonly menu: HTMLElement;
  setResults(records: ScoreRecord[]): void;
}

export function createControls(host: HTMLElement, handlers: ControlHandlers): ControlsApi {
  host.replaceChildren();

  // ── Transport (top bar) ──────────────────────────────────────────────────
  // Song picker — a grouped selector that lives in the drawer (the top bar shows
  // only the current title). ←/→ cycles it from the bench; click works with a mouse.
  const catalog = el<HTMLSelectElement>("select");
  catalog.onchange = () => {
    if (catalog.value) handlers.onCatalogPick(catalog.value);
  };
  const songTitle = el<HTMLSpanElement>("span", {
    class: "song-title",
    textContent: "— No song —",
  });
  let catalogEntries: CatalogEntry[] = [];
  let cachedSongIds = new Set<string>();
  /** The loaded song's base tempo (BPM) for the display-only readout; null = unknown. */
  let songBaseBpm: number | null = null;

  const playNext = el<HTMLButtonElement>("button", {
    class: "primary",
    title: "Curriculum suggestion",
  });
  let playNextId = "";
  playNext.style.display = "none";
  playNext.onclick = () => {
    if (playNextId) handlers.onPlayNext(playNextId);
  };

  // Transport is keyboard-driven; the bar only reflects play state. The clickable
  // play/pause control lives in the drawer (Practice) for mouse / no-MIDI use.
  const playStatus = el<HTMLSpanElement>("span", {
    class: "play-status",
    textContent: "❚❚ Paused",
  });
  // The effective play mode (Wait / Play / Coach / Demo), always visible in the bar so
  // a wait-mode pause (gating for input) can't be mistaken for a freeze.
  const modeStatus = el<HTMLSpanElement>("span", {
    class: "readout mode-status",
    textContent: "Wait",
  });
  const chainDots = buildChainDots();
  const playBtn = el<HTMLButtonElement>("button", { class: "primary", textContent: "▶ Play" });
  playBtn.onclick = () => handlers.onPlayPause();
  const restartBtn = el<HTMLButtonElement>("button", { textContent: "⟲ Restart" });
  restartBtn.onclick = () => handlers.onRestart();

  const tempo = el<HTMLInputElement>("input", {
    type: "range",
    id: "tempo",
    min: "25",
    max: "150",
    value: "100",
  });
  const tempoLabel = el<HTMLSpanElement>("span", { textContent: "100%" });
  // Top-bar tempo is a plain readout (BPM); the slider control lives in the drawer.
  const tempoReadout = el<HTMLSpanElement>("span", { class: "readout", textContent: "100%" });
  const metroStatus = el<HTMLSpanElement>("span", {
    class: "readout metro-status",
    textContent: "🥁 Off",
  });
  tempo.oninput = () => {
    // The label is owned by setTempo (round-tripped through onTempo) so it can
    // render absolute BPM, not a raw percentage.
    handlers.onTempo(Number(tempo.value) / 100);
  };

  const loop = buildLoopControls(handlers);

  const mapBtn = el<HTMLButtonElement>("button", {
    class: "primary",
    textContent: "🗺 Discovery Map",
  });
  mapBtn.onclick = () => handlers.onOpenMap();
  const pathBtn = el<HTMLButtonElement>("button", { textContent: "🧭 Path to this song" });
  pathBtn.onclick = () => handlers.onShowPath();

  const status = el<HTMLSpanElement>("span", { id: "status", textContent: "Loading…" });
  const midiStatus = el<HTMLSpanElement>("span", { id: "midi-status", textContent: "⚠ No MIDI" });
  midiStatus.style.cssText = "color:#ff5a5a;white-space:nowrap";
  const menuBtn = el<HTMLButtonElement>("button", {
    class: "menu-btn",
    textContent: "☰",
    title: "Menu",
  });

  // ── Drawer (config + results) ────────────────────────────────────────────
  const mode = selectOf([
    ["wait", "Wait mode (learn)"],
    ["scored", "Scored mode (play through)"],
    ["coach", "Coach (auto-practice)"],
  ]);
  mode.onchange = () => handlers.onMode(mode.value as UIMode);

  // ── Coach params (drawer "Coach" section) ──
  const coachSingle = checkbox("Single-chain (debug)");
  coachSingle.input.onchange = () =>
    handlers.onCoachConfig({ singleChain: coachSingle.input.checked });
  const coachFullRun = checkbox("Whole-piece final run");
  coachFullRun.input.onchange = () =>
    handlers.onCoachConfig({ requireFullRun: coachFullRun.input.checked });
  const coachNums = buildCoachNumFields(handlers.onCoachConfig);

  const hand = selectOf([
    ["both", "Both hands"],
    ["left", "Left hand"],
    ["right", "Right hand"],
  ]);
  hand.onchange = () => handlers.onHand(hand.value as HandFilter);

  const swapHands = checkbox("Auto-split: swap hands");
  const splitBias = el<HTMLInputElement>("input", {
    type: "range",
    min: "-5",
    max: "5",
    value: "0",
  });
  const splitBiasLabel = el<HTMLSpanElement>("span", { textContent: "0" });
  const emitOverride = () => {
    splitBiasLabel.textContent = splitBias.value;
    handlers.onHandOverride({
      swap: swapHands.input.checked,
      biasSemitones: Number(splitBias.value),
    });
  };
  swapHands.input.onchange = emitOverride;
  splitBias.oninput = emitOverride;

  const device = el<HTMLSelectElement>("select");
  device.onchange = () => handlers.onDevice(device.value);
  // Only shown when there are 2+ real devices to choose between (see setDevices).
  const deviceField = field("Device", device);

  const fileInput = el<HTMLInputElement>("input", { type: "file", accept: ".mid,.midi" });
  fileInput.onchange = () => {
    const file = fileInput.files?.[0];
    if (file) handlers.onFile(file);
  };

  const cal = buildCalibrationControls(handlers);

  const transpose = el<HTMLInputElement>("input", {
    type: "range",
    min: "-12",
    max: "12",
    value: "0",
  });
  const transposeLabel = el<HTMLSpanElement>("span", { textContent: "0" });
  transpose.oninput = () => {
    transposeLabel.textContent = transpose.value;
    handlers.onTranspose(Number(transpose.value));
  };

  // Metronome (Practice section): toggle + click-volume + count-in (own module).
  const metro = buildMetronomeControls(handlers);
  const record = buildRecordControl(handlers.onToggleRecord); // performance recorder → MIDI export
  // MIDI-out playback (F1): accompaniment toggle + a "play it for me" listen-through.
  const accompaniment = checkbox("Accompaniment (sound the unplayed hand via MIDI-out)");
  accompaniment.input.onchange = () => handlers.onAccompaniment(accompaniment.input.checked);
  const playForMe = el<HTMLButtonElement>("button", { textContent: "▶ Play it for me" });
  playForMe.onclick = () => handlers.onPlayForMe();

  const showKeyboard = checkbox("Show keyboard keys");
  const slotLabels = checkbox("Slot letters (C D E …)");
  const noteLabels = checkbox("Note letters on notes");
  const rotate180 = checkbox("Rotate 180° (upside-down mount)");
  const fingerLabels = checkbox("Finger numbers on notes");
  const staff = buildStaffControls();
  const emitDisplay = () =>
    handlers.onDisplay({
      showKeyboard: showKeyboard.input.checked,
      slotLabels: slotLabels.input.checked,
      noteLabels: noteLabels.input.checked,
      fingerLabels: fingerLabels.input.checked,
      rotate180: rotate180.input.checked,
      ...staff.read(),
    });
  for (const c of [showKeyboard, slotLabels, noteLabels, fingerLabels, rotate180]) {
    c.input.onchange = emitDisplay;
  }
  staff.bind(emitDisplay);

  const pianoLiveKeys = checkbox("Live keys (bottom keys control transport)");
  pianoLiveKeys.input.onchange = () => handlers.onPianoLiveKeys(pianoLiveKeys.input.checked);

  const results = el<HTMLDivElement>("div", { class: "results" });
  const clearBtn = el<HTMLButtonElement>("button", { textContent: "Clear results" });
  clearBtn.onclick = () => handlers.onClearResults();

  const closeBtn = el<HTMLButtonElement>("button", {
    class: "menu-btn",
    textContent: "✕",
    title: "Close",
  });
  const nowPlaying = el<HTMLDivElement>("div", { class: "now-playing" });
  const drawer = el<HTMLDivElement>("div", { class: "drawer" });
  drawer.append(
    rowBetween(el("strong", { textContent: "Menu" }), closeBtn),
    buildKeyHints(), // command keys (left) · always-on keys (right), at the very top
    el("h3", { textContent: "Song" }),
    playNext,
    field("Pick", catalog),
    mapBtn,
    pathBtn,
    nowPlaying,
    el("h3", { textContent: "Config" }),
    field("Mode", mode),
    field("Hands", hand),
    swapHands.wrap,
    fieldWith("Split bias", splitBias, splitBiasLabel),
    deviceField,
    field("Load .mid", fileInput),
    ...cal.rows,
    fieldWith("Transpose", transpose, transposeLabel),
    el("h3", { textContent: "Practice" }),
    playBtn,
    fieldWith("Tempo", tempo, tempoLabel),
    ...metro.rows,
    record.row,
    accompaniment.wrap,
    playForMe,
    el("h3", { textContent: "Coach" }),
    coachSingle.wrap,
    coachFullRun.wrap,
    ...coachNums.flatMap((c) => {
      const row = fieldWith(c.p.label, c.input, c.label);
      return c.warn ? [row, c.warn] : [row];
    }),
    el("h3", { textContent: "Display" }),
    showKeyboard.wrap,
    slotLabels.wrap,
    noteLabels.wrap,
    rotate180.wrap,
    fingerLabels.wrap,
    ...staff.rows,
    pianoLiveKeys.wrap,
    el("h3", { textContent: "Results" }),
    results,
    clearBtn,
    ...buildCredits(),
  );

  const toggle = () => drawer.classList.toggle("open");
  menuBtn.onclick = toggle;
  closeBtn.onclick = toggle;

  // Secondary transport: hidden on narrow screens (mobile = demo) so the bar
  // doesn't overflow. Essentials kept: song title, play, MIDI indicator, menu.
  const loopGroup = loop.group;
  for (const node of [restartBtn, tempoReadout, metroStatus, loopGroup, status]) {
    node.classList.add("bar-secondary");
  }

  host.append(
    group("Song", songTitle),
    modeStatus,
    chainDots.el,
    playStatus,
    restartBtn,
    tempoReadout,
    metroStatus,
    loopGroup,
    status,
    midiStatus,
    menuBtn,
  );
  document.body.append(drawer);

  return {
    setCatalog(entries) {
      catalogEntries = entries;
      renderCatalogOptions(catalog, entries, cachedSongIds);
    },
    setCached(ids) {
      cachedSongIds = ids;
      repaintCatalogCache(catalog, catalogEntries, cachedSongIds);
    },
    setPlayNext(text, id) {
      playNextId = id ?? "";
      if (text === null) {
        playNext.style.display = "none";
      } else {
        playNext.textContent = text;
        playNext.style.display = "";
      }
    },
    setDevices(ports, selectedId) {
      renderDeviceOptions(device, deviceField, ports, selectedId);
    },
    setPlaying(playing) {
      playBtn.textContent = playing ? "❚❚ Pause" : "▶ Play";
      playStatus.textContent = playing ? "▶ Playing" : "❚❚ Paused";
      playStatus.classList.toggle("on", playing);
    },
    setStatus(text) {
      status.textContent = text;
    },
    setMidi(deviceName) {
      midiStatus.textContent = deviceName ? `🎹 ${shortMidiName(deviceName)}` : "⚠ No MIDI";
      midiStatus.style.color = deviceName ? "#3ddc84" : "#ff5a5a";
    },
    setNowPlaying(attribution) {
      nowPlaying.textContent = attribution;
    },
    setCurrentSong(title) {
      songTitle.textContent = title || "— No song —";
    },
    setSelectedSong(id) {
      catalog.value = id; // non-catalog id (file import) has no <option> → falls back to ""
    },
    setCalibration(whiteWidthPx, offsetPx) {
      cal.set(whiteWidthPx, offsetPx);
    },
    setMode(value) {
      mode.value = value;
    },
    setModeLabel(label) {
      modeStatus.textContent = label;
    },
    setChainDots(chains) {
      chainDots.set(chains);
    },
    setTempo(multiplier) {
      tempo.value = String(Math.round(multiplier * 100));
      const label = formatTempoLabel(songBaseBpm, multiplier);
      tempoLabel.textContent = label;
      tempoReadout.textContent = label;
    },
    setSongBpm(bpm) {
      songBaseBpm = bpm;
    },
    setLoop(start, end) {
      loop.setLoop(start, end);
    },
    setTranspose(semitones) {
      transpose.value = String(semitones);
      transposeLabel.textContent = String(semitones);
    },
    setMetronome(on) {
      metro.setEnabled(on);
      metroStatus.textContent = on ? "🥁 On" : "🥁 Off";
      metroStatus.classList.toggle("on", on);
    },
    setMetronomeVolume(volume) {
      metro.setVolume(volume);
    },
    setCountIn(beats) {
      metro.setCountIn(beats);
    },
    setAccompaniment(on) {
      accompaniment.input.checked = on;
    },
    setRecording(recording, enabled) {
      record.setState(recording, enabled);
    },
    setCoachConfig(cfg) {
      coachSingle.input.checked = cfg.singleChain;
      coachFullRun.input.checked = cfg.requireFullRun;
      for (const c of coachNums) {
        const v = cfg[c.p.key];
        c.input.value = String(v);
        c.label.textContent = String(v);
        c.updateWarn(Number(v));
      }
    },
    setHand(value) {
      hand.value = value;
    },
    setHandOverride(opts) {
      swapHands.input.checked = opts.swap;
      splitBias.value = String(opts.biasSemitones);
      splitBiasLabel.textContent = String(opts.biasSemitones);
    },
    setDisplay(display) {
      showKeyboard.input.checked = display.showKeyboard;
      slotLabels.input.checked = display.slotLabels;
      noteLabels.input.checked = display.noteLabels;
      rotate180.input.checked = display.rotate180;
      fingerLabels.input.checked = display.fingerLabels;
      staff.set(display);
    },
    setPianoLiveKeys(on) {
      pianoLiveKeys.input.checked = on;
    },
    menu: drawer,
    setResults(records) {
      renderResults(results, records);
    },
  };
}
